import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { AnimatePresence, motion, useMotionValue, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Minus, Plus, RotateCcw, X, ZoomIn } from "lucide-react";
import { Container } from "@/components/shared/PageHeader";
import { PhotoCredit } from "@/components/shared/PhotoCredit";
import { PhotoNumberTag } from "@/components/shared/PhotoNumber";
import { imgSrcSet, imgUrl, LQIP_WIDTH } from "@/lib/api";
import { cn } from "@/lib/utils";

/*
 * The archive collage as the star of «Om huset».
 *
 * ArchivePanorama – one wide photograph, edge to edge. It is drawn to *cover* its frame; whatever does not fit
 *   (the sides on a phone, a sliver of top and bottom on a wide screen) is revealed while you scroll past, so the
 *   whole collage is seen without touching it. On a desktop a loupe follows the pointer and magnifies the small
 *   photographs underneath it. Clicking anywhere opens the viewer.
 * ArchiveDetails – square close-ups cut from the same photograph (pure CSS, no extra files). Each opens the viewer
 *   zoomed in on that spot.
 * ZoomViewer – full-screen: drag to pan, wheel/pinch to zoom, double-click to jump in and out.
 *
 * Everything moves with transform/opacity only; under prefers-reduced-motion the panorama is simply centred.
 */

const ease = [0.2, 0.8, 0.2, 1];
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const clamp01 = (v) => clamp(v, 0, 1);
const DEFAULT_RATIO = 8 / 3;
const LENS = 240; // loupe diameter (px)
const LENS_ZOOM = 2.2;
const MAX_ZOOM = 6;

/** Watches an element's size. */
const useSize = () => {
  const [el, setEl] = useState(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, [el]);
  return [setEl, size, el];
};

/* ----------------------------------------------------------------------------- panorama */
export const ArchivePanorama = ({ src, alt = "", caption, onOpen, ratio = DEFAULT_RATIO, className, testId = "archive-panorama" }) => {
  const reduce = useReducedMotion();
  const frameRef = useRef(null);
  const [setFrameEl, frame] = useSize();
  const setFrame = useCallback((el) => { frameRef.current = el; setFrameEl(el); }, [setFrameEl]);
  const [nat, setNat] = useState(ratio);
  const [ready, setReady] = useState(false);
  const [lens, setLens] = useState(false);

  // Cover geometry: the picture is as large as it must be to fill the frame; `over*` is what hangs outside.
  const cover = frame.w / Math.max(frame.h, 1) >= nat ? { w: frame.w, h: frame.w / nat } : { w: frame.h * nat, h: frame.h };
  const overX = Math.max(0, cover.w - frame.w);
  const overY = Math.max(0, cover.h - frame.h);

  // Scroll reveals the hidden part – a slow pan on phones, a gentle drift on wide screens.
  const { scrollYProgress } = useScroll({ target: frameRef, offset: ["start end", "end start"] });
  const ox = useMotionValue(0);
  const oy = useMotionValue(0);
  useEffect(() => { ox.set(overX); oy.set(overY); }, [overX, overY, ox, oy]);
  const x = useTransform([scrollYProgress, ox], ([p, o]) => (reduce ? -o / 2 : -o * clamp01((p - 0.12) / 0.76)));
  const y = useTransform([scrollYProgress, oy], ([p, o]) => (reduce ? -o / 2 : -o * clamp01((p - 0.12) / 0.76)));

  // The loupe follows the pointer exactly; inside it the same picture is drawn LENS_ZOOM times larger.
  const lx = useMotionValue(0);
  const ly = useMotionValue(0);
  const lensX = useTransform(lx, (v) => v - LENS / 2);
  const lensY = useTransform(ly, (v) => v - LENS / 2);
  const innerX = useTransform([lx, x], ([l, xx]) => LENS / 2 - (l - xx) * LENS_ZOOM);
  const innerY = useTransform([ly, y], ([l, yy]) => LENS / 2 - (l - yy) * LENS_ZOOM);
  const onMove = (e) => {
    if (e.pointerType !== "mouse" || !frameRef.current) return;
    const r = frameRef.current.getBoundingClientRect();
    lx.set(e.clientX - r.left);
    ly.set(e.clientY - r.top);
    if (!lens) setLens(true);
  };
  const onLeave = () => setLens(false);

  const onLoad = (e) => {
    const el = e.currentTarget;
    if (el.naturalWidth && el.naturalHeight) setNat(el.naturalWidth / el.naturalHeight);
    setReady(true);
  };

  if (!src) return null;
  const measured = frame.w > 0 && frame.h > 0;
  const sizes = measured ? `${Math.round(cover.w)}px` : "100vw";

  return (
    <section className={cn("overflow-x-clip", className)} data-testid={testId}>
      <motion.button
        ref={setFrame}
        type="button"
        onClick={() => onOpen?.()}
        onPointerMove={onMove}
        onPointerLeave={onLeave}
        className={cn(
          "relative block h-[300px] w-full overflow-hidden bg-ink text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sol sm:h-[360px] lg:h-[min(37.5vw,78vh,760px)]",
          lens ? "cursor-none" : "cursor-zoom-in"
        )}
        aria-label="Opne arkivbiletet i stor storleik"
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, ease }}
        data-testid={`${testId}-button`}
      >
        {/* blur-up placeholder – a few hundred bytes, paints at once */}
        <img src={imgUrl(src, LQIP_WIDTH)} alt="" aria-hidden="true" loading="eager" decoding="async" className="absolute inset-0 h-full w-full scale-110 object-cover blur-2xl" />

        {/* the picture – settles in from a hair larger, then follows the scroll */}
        <motion.div
          className="absolute inset-0 will-change-transform"
          initial={reduce ? false : { scale: 1.04 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease, delay: 0.1 }}
          aria-hidden="true"
        >
          {measured && (
            <motion.img
              src={imgUrl(src, 2000)}
              srcSet={imgSrcSet(src)}
              sizes={sizes}
              alt=""
              loading="eager"
              decoding="async"
              fetchPriority="high"
              onLoad={onLoad}
              onError={() => setReady(true)}
              style={{ width: cover.w, height: cover.h, x, y }}
              className={cn("absolute left-0 top-0 max-w-none transition-opacity duration-700 ease-out will-change-transform", ready ? "opacity-100" : "opacity-0")}
              data-testid={`${testId}-image`}
              data-ready={ready ? "true" : "false"}
            />
          )}
        </motion.div>

        {/* a quiet inner hairline – the frame's second rule */}
        <span aria-hidden="true" className="pointer-events-none absolute inset-2.5 border border-paper/20" />

        {/* the loupe */}
        <AnimatePresence>
          {lens && ready && (
            <motion.div
              key="lens"
              className="pointer-events-none absolute left-0 top-0 z-10 overflow-hidden rounded-full bg-ink shadow-lift ring-1 ring-sol/70 will-change-transform"
              style={{ width: LENS, height: LENS, x: lensX, y: lensY }}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.18, ease }}
              aria-hidden="true"
              data-testid={`${testId}-lens`}
            >
              <motion.img src={imgUrl(src, 2000)} alt="" style={{ width: cover.w * LENS_ZOOM, height: cover.h * LENS_ZOOM, x: innerX, y: innerY }} className="absolute left-0 top-0 max-w-none" />
              <span className="absolute inset-0 rounded-full ring-[3px] ring-inset ring-paper/90" />
              <span className="absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-sol shadow-[0_0_10px_2px_hsl(var(--sol)/0.5)]" />
            </motion.div>
          )}
        </AnimatePresence>
        <span className="sr-only">{alt}</span>
      </motion.button>

      <Container>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-x-8 gap-y-3 text-[13px] text-muted-foreground" data-testid={`${testId}-caption`}>
          <p className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
            <span>{caption}</span>
            <PhotoCredit src={src} testId={`${testId}-credit`} />
            <PhotoNumberTag src={src} interactive tone="muted" testId={`${testId}-number`} />
            <span className="t-mono-sm hidden md:inline">Hald peikaren over biletet for å sjå nærare</span>
          </p>
          <button
            type="button"
            onClick={() => onOpen?.()}
            className="inline-flex h-10 items-center gap-2 rounded-full border border-foreground/20 px-4 text-[14px] font-medium text-foreground transition-colors hover:border-foreground hover:bg-foreground hover:text-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2"
            data-testid={`${testId}-open`}
          >
            <ZoomIn className="h-4 w-4" />Sjå nærare
          </button>
        </div>
      </Container>
    </section>
  );
};

/* ------------------------------------------------------------------------------ details */
/** Where the close-ups are cut (fractions of the picture: left, top, width – the tile is square). */
export const ARCHIVE_DETAILS = [
  { x: 0.215, y: 0.63, w: 0.13 },
  { x: 0.86, y: 0.0, w: 0.135 },
  { x: 0.87, y: 0.35, w: 0.125 },
  { x: 0.0, y: 0.0, w: 0.125 },
];

export const ArchiveDetails = ({ src, details = ARCHIVE_DETAILS, onOpen, testId = "archive-details" }) => {
  if (!src) return null;
  return (
    <ul className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid={testId}>
      {details.map((d, i) => (
        <li key={`${d.x}-${d.y}`}>
          <button
            type="button"
            onClick={() => onOpen?.(d)}
            className="group relative block aspect-square w-full overflow-hidden rounded-[12px] bg-secondary ring-1 ring-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2"
            aria-label={`Sjå utsnitt ${i + 1} av arkivbiletet nært`}
            data-testid={`${testId}-${i}`}
          >
            <span className="absolute inset-0 transition-transform duration-700 ease-house group-hover:scale-[1.04]">
              <img
                src={imgUrl(src, 2000)}
                alt=""
                loading="lazy"
                decoding="async"
                className="absolute left-0 top-0 max-w-none"
                style={{ width: `${100 / d.w}%`, height: "auto", transform: `translate(${-d.x * 100}%, ${-d.y * 100}%)` }}
              />
            </span>
            <span className="t-mono-sm absolute bottom-3 left-3 rounded-full bg-ink/70 px-2 py-1 text-paper/90 tabular backdrop-blur-sm" aria-hidden="true">0{i + 1}</span>
            <span className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-paper/90 text-ink opacity-0 shadow-soft transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden="true"><ZoomIn className="h-3.5 w-3.5" /></span>
          </button>
        </li>
      ))}
    </ul>
  );
};

/* ------------------------------------------------------------------------------- viewer */
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

export const ZoomViewer = ({ src, alt = "", open, focus, onOpenChange, ratio = DEFAULT_RATIO, testId = "archive-viewer" }) => {
  const [setStageEl, stage, stageEl] = useSize();
  const [nat, setNat] = useState(ratio);
  const [loaded, setLoaded] = useState(false);
  const [t, setT] = useState({ s: 1, x: 0, y: 0 });
  const ptrs = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef(0);
  const applied = useRef(false);

  // The picture is fitted inside the stage with a margin, so the bars never cover it at 100 %.
  const pad = stage.w < 640 ? 16 : 56;
  const area = { w: Math.max(stage.w - pad * 2, 1), h: Math.max(stage.h - pad * 2 - 72, 1) };
  const fit = area.w / area.h >= nat ? { w: area.h * nat, h: area.h } : { w: area.w, h: area.w / nat };

  const constrain = useCallback((n) => {
    const maxX = Math.max(0, (fit.w * n.s - stage.w) / 2);
    const maxY = Math.max(0, (fit.h * n.s - stage.h) / 2);
    return { s: n.s, x: clamp(n.x, -maxX, maxX), y: clamp(n.y, -maxY, maxY) };
  }, [fit.w, fit.h, stage.w, stage.h]);

  /** Zoom by factor `f` around a point (px, py) measured from the stage centre. */
  const zoomAt = useCallback((f, px = 0, py = 0) => {
    setT((o) => {
      const s = clamp(o.s * f, 1, MAX_ZOOM);
      const k = s / o.s;
      return constrain({ s, x: px - (px - o.x) * k, y: py - (py - o.y) * k });
    });
  }, [constrain]);

  const reset = useCallback(() => setT({ s: 1, x: 0, y: 0 }), []);

  // On open: start on the detail that was clicked, or on the whole picture.
  useEffect(() => { if (!open) { applied.current = false; setLoaded(false); } }, [open]);
  useEffect(() => {
    if (!open || applied.current || !stage.w || !stage.h) return;
    applied.current = true;
    if (!focus) {
      // Phones: a wide picture fitted to a narrow screen is a thin strip – start a little closer, centred.
      if (stage.w < 640 && fit.h < stage.h * 0.5) { const s = clamp((stage.h * 0.55) / fit.h, 1, MAX_ZOOM); setT(constrain({ s, x: 0, y: 0 })); } else reset();
      return;
    }
    const fh = focus.w * nat;
    const s = clamp(0.72 * Math.min(stage.w / (focus.w * fit.w), stage.h / (fh * fit.h)), 1, MAX_ZOOM);
    const cx = (focus.x + focus.w / 2 - 0.5) * fit.w;
    const cy = (focus.y + fh / 2 - 0.5) * fit.h;
    setT(constrain({ s, x: -cx * s, y: -cy * s }));
  }, [open, focus, stage.w, stage.h, fit.w, fit.h, nat, constrain, reset]);

  // Wheel must be a non-passive listener to stop the page behind from scrolling.
  useEffect(() => {
    if (!stageEl) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const r = stageEl.getBoundingClientRect();
      zoomAt(Math.exp(-e.deltaY * 0.0016), e.clientX - r.left - r.width / 2, e.clientY - r.top - r.height / 2);
    };
    stageEl.addEventListener("wheel", onWheel, { passive: false });
    return () => stageEl.removeEventListener("wheel", onWheel);
  }, [stageEl, zoomAt]);

  const rel = (e) => { const r = stageEl.getBoundingClientRect(); return { x: e.clientX - r.left - r.width / 2, y: e.clientY - r.top - r.height / 2 }; };

  const onPointerDown = (e) => {
    if (!stageEl) return;
    stageEl.setPointerCapture?.(e.pointerId);
    ptrs.current.set(e.pointerId, rel(e));
    const pts = [...ptrs.current.values()];
    if (pts.length === 1) gesture.current = { type: "pan", start: pts[0], x0: t.x, y0: t.y, moved: false };
    else if (pts.length === 2) gesture.current = { type: "pinch", d0: dist(pts[0], pts[1]), m0: mid(pts[0], pts[1]), s0: t.s, x0: t.x, y0: t.y, moved: true };
  };
  const onPointerMove = (e) => {
    if (!ptrs.current.has(e.pointerId) || !gesture.current) return;
    ptrs.current.set(e.pointerId, rel(e));
    const g = gesture.current;
    const pts = [...ptrs.current.values()];
    if (g.type === "pan" && pts.length === 1) {
      const dx = pts[0].x - g.start.x, dy = pts[0].y - g.start.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) g.moved = true;
      setT((o) => constrain({ s: o.s, x: g.x0 + dx, y: g.y0 + dy }));
    } else if (g.type === "pinch" && pts.length === 2) {
      const d = dist(pts[0], pts[1]), m = mid(pts[0], pts[1]);
      const s = clamp(g.s0 * (d / Math.max(g.d0, 1)), 1, MAX_ZOOM);
      const k = s / g.s0;
      setT(constrain({ s, x: m.x - (g.m0.x - g.x0) * k, y: m.y - (g.m0.y - g.y0) * k }));
    }
  };
  const onPointerUp = (e) => {
    const g = gesture.current;
    const wasTap = g?.type === "pan" && !g.moved;
    ptrs.current.delete(e.pointerId);
    const pts = [...ptrs.current.values()];
    if (pts.length === 1) gesture.current = { type: "pan", start: pts[0], x0: t.x, y0: t.y, moved: true };
    else if (pts.length === 0) gesture.current = null;
    if (wasTap && e.pointerType === "touch") {
      const now = Date.now();
      if (now - lastTap.current < 320) { const p = rel(e); if (t.s > 1.05) reset(); else zoomAt(2.6, p.x, p.y); lastTap.current = 0; }
      else lastTap.current = now;
    }
  };
  const onDoubleClick = (e) => { if (!stageEl) return; const p = rel(e); if (t.s > 1.05) reset(); else zoomAt(2.6, p.x, p.y); };

  const dragging = Boolean(gesture.current);
  const pct = Math.round(t.s * 100);
  const btn = "flex h-10 w-10 items-center justify-center rounded-full text-paper transition-colors hover:bg-paper hover:text-ink disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-[101] flex flex-col text-paper outline-none duration-200 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0"
          aria-describedby={undefined}
          data-testid={testId}
        >
          <DialogPrimitive.Title className="sr-only">{alt || "Arkivbiletet"}</DialogPrimitive.Title>

          {/* stage */}
          <div
            ref={setStageEl}
            className={cn("relative flex-1 touch-none select-none overflow-hidden", dragging ? "cursor-grabbing" : t.s > 1.05 ? "cursor-grab" : "cursor-zoom-in")}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onDoubleClick={onDoubleClick}
            data-testid={`${testId}-stage`}
          >
            {src && stage.w > 0 && (
              <>
                <img
                  src={imgUrl(src, LQIP_WIDTH)}
                  alt=""
                  aria-hidden="true"
                  draggable={false}
                  className={cn("absolute max-w-none blur-xl transition-opacity duration-500", loaded ? "opacity-0" : "opacity-100")}
                  style={{ width: fit.w, height: fit.h, left: (stage.w - fit.w) / 2, top: (stage.h - fit.h) / 2, transform: `translate(${t.x}px, ${t.y}px) scale(${t.s})` }}
                />
                <img
                  src={imgUrl(src, 2000)}
                  alt={alt}
                  draggable={false}
                  decoding="async"
                  onLoad={(e) => { const el = e.currentTarget; if (el.naturalWidth && el.naturalHeight) setNat(el.naturalWidth / el.naturalHeight); setLoaded(true); }}
                  className={cn("absolute max-w-none will-change-transform", loaded ? "opacity-100" : "opacity-0", !dragging && "transition-transform duration-300 ease-house")}
                  style={{ width: fit.w, height: fit.h, left: (stage.w - fit.w) / 2, top: (stage.h - fit.h) / 2, transform: `translate(${t.x}px, ${t.y}px) scale(${t.s})` }}
                  data-testid={`${testId}-image`}
                />
              </>
            )}
          </div>

          {/* top bar */}
          <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-4 bg-gradient-to-b from-ink/70 to-transparent px-4 pb-10 pt-4 sm:px-6 sm:pt-5">
            <div className="pointer-events-auto min-w-0">
              <p className="t-mono text-paper/60">Frå arkivet</p>
              <p className="mt-1 truncate font-display text-[17px] font-semibold tracking-[-0.01em]">{alt}</p>
            </div>
            <DialogPrimitive.Close className="pointer-events-auto flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-paper/30 text-paper transition-colors hover:bg-paper hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sol" aria-label="Lukk" data-testid={`${testId}-close`}>
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          {/* controls */}
          <div className="safe-bottom pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center gap-3 bg-gradient-to-t from-ink/70 to-transparent px-4 pb-4 pt-10 sm:pb-6">
            <div className="pointer-events-auto flex items-center gap-1 rounded-full bg-ink/80 p-1 shadow-lift ring-1 ring-paper/15 backdrop-blur-md" data-testid={`${testId}-controls`}>
              <button type="button" onClick={() => zoomAt(1 / 1.5)} disabled={t.s <= 1.001} className={btn} aria-label="Zoom ut" data-testid={`${testId}-zoom-out`}><Minus className="h-4 w-4" /></button>
              <span className="min-w-[5.5ch] whitespace-nowrap text-center text-[13px] font-medium tabular" aria-live="polite" data-testid={`${testId}-zoom`}>{pct} %</span>
              <button type="button" onClick={() => zoomAt(1.5)} disabled={t.s >= MAX_ZOOM - 0.001} className={btn} aria-label="Zoom inn" data-testid={`${testId}-zoom-in`}><Plus className="h-4 w-4" /></button>
              <span className="mx-1 h-5 w-px bg-paper/20" aria-hidden="true" />
              <button type="button" onClick={reset} disabled={t.s <= 1.001} className={btn} aria-label="Vis heile biletet" data-testid={`${testId}-reset`}><RotateCcw className="h-4 w-4" /></button>
            </div>
            <p className="t-mono-sm text-paper/55"><span className="hidden sm:inline">Dra for å flytte · rull for å zoome · dobbeltklikk for å gå inn</span><span className="sm:hidden">Dra for å flytte · klyp for å zoome</span></p>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
